import React, {createContext, useContext, useEffect, useState, useCallback} from "react";
import {
  User as FirebaseUser,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  ActionCodeSettings,
} from "firebase/auth";
import {auth} from "../firebase";

// Check if auth is available
if (!auth) {
  console.error("Firebase Auth is not initialized. Please configure Firebase.");
}

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  sessionTimeRemaining: number | null; // Time remaining in milliseconds
  sendLoginLink: (email: string) => Promise<void>;
  verifyEmailLink: (email: string, emailLink: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Session expiration: 1 hour in milliseconds
const SESSION_DURATION = 60 * 60 * 1000; // 1 hour
const SESSION_STORAGE_KEY = "sessionStartTime";

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(null);

  // Check if session has expired
  const checkSessionExpiry = useCallback(async () => {
    if (!auth || !user) return;

    const sessionStartTime = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionStartTime) {
      // No session time stored, consider it expired
      await signOut(auth);
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }

    const sessionStart = parseInt(sessionStartTime, 10);
    const now = Date.now();
    const sessionAge = now - sessionStart;

    if (sessionAge >= SESSION_DURATION) {
      // Session expired
      console.log("Session expired after 6 hours");
      await signOut(auth);
      localStorage.removeItem(SESSION_STORAGE_KEY);
      // Show message to the user
      alert("Your session has expired after 1 hour. Please sign in again.");
    }
  }, [user]);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User signed in - check if we need to store session time
        const existingSessionTime = localStorage.getItem(SESSION_STORAGE_KEY);
        if (!existingSessionTime) {
          // New session - store start time
          localStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
          setSessionTimeRemaining(SESSION_DURATION);
        } else {
          // Existing session - check if expired
          const sessionStart = parseInt(existingSessionTime, 10);
          const sessionAge = Date.now() - sessionStart;
          const remaining = SESSION_DURATION - sessionAge;
          
          if (remaining <= 0) {
            // Already expired
            if (auth) {
              await signOut(auth);
            }
            localStorage.removeItem(SESSION_STORAGE_KEY);
            setSessionTimeRemaining(null);
            alert("Your session has expired after 1 hour. Please sign in again.");
          } else {
            setSessionTimeRemaining(remaining);
          }
        }
      } else {
        // User signed out - clear session time
        localStorage.removeItem(SESSION_STORAGE_KEY);
        setSessionTimeRemaining(null);
      }
      
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Periodically check session expiry and update remaining time (every minute)
  useEffect(() => {
    if (!auth || !user) {
      setSessionTimeRemaining(null);
      return;
    }

    const updateSessionTime = () => {
      const sessionStartTime = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!sessionStartTime) {
        setSessionTimeRemaining(null);
        return;
      }

      const sessionStart = parseInt(sessionStartTime, 10);
      const sessionAge = Date.now() - sessionStart;
      const remaining = SESSION_DURATION - sessionAge;

      if (remaining <= 0) {
        // Session expired
        checkSessionExpiry();
        setSessionTimeRemaining(0);
      } else {
        setSessionTimeRemaining(remaining);
      }
    };

    // Update immediately
    updateSessionTime();

    // Then update every minute
    const interval = setInterval(updateSessionTime, 60 * 1000); // 1 minute

    return () => clearInterval(interval);
  }, [user, checkSessionExpiry]);

  // Check if we're returning from an email link
  useEffect(() => {
    if (!auth) return;

    const checkEmailLink = async () => {
      if (auth && isSignInWithEmailLink(auth, window.location.href)) {
        setLoading(true);
        // Get email from localStorage or prompt
        const email = window.localStorage.getItem("emailForSignIn");
        if (email) {
          try {
            await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem("emailForSignIn");
            // Clean up URL
            window.history.replaceState({}, document.title, "/dashboard");
          } catch (error: any) {
            console.error("Error signing in with email link:", error);
          } finally {
            setLoading(false);
          }
        }
      }
    };

    checkEmailLink();
  }, []);

  const sendLoginLink = async (email: string) => {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }

    // Use current origin to support custom domains
    const currentOrigin = window.location.origin;
    
    const actionCodeSettings: ActionCodeSettings = {
      url: currentOrigin + "/dashboard",
      handleCodeInApp: true,
    };

    // Save email for verification
    window.localStorage.setItem("emailForSignIn", email);

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  };

  const verifyEmailLink = async (email: string, emailLink: string) => {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }

    await signInWithEmailLink(auth, email, emailLink);
    window.localStorage.removeItem("emailForSignIn");
    // Store session start time on successful sign-in
    localStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
  };

  const logout = async () => {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }

    await signOut(auth);
    // Clear session time on logout
    localStorage.removeItem(SESSION_STORAGE_KEY);
  };

  const value = {
    user,
    loading,
    sessionTimeRemaining,
    sendLoginLink,
    verifyEmailLink,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

